/**
 * Seat roster panel — the DOM mirror of room presence, plus the GM's
 * per-seat management affordances (phase1-plan §4; test-spec §5.2, §6.2).
 *
 * Superset of the M1 seat panel: it keeps that panel's exact mirror
 * contract (`data-testid="seat-list"`, `data-seat-id`, `data-role`,
 * `data-connected`, `data-name`) so E2E-01/E2E-12 keep passing, and adds:
 *   • a `reconnecting` state (grace window) distinct from plain "away";
 *   • a GM-only "Remove" button per OTHER seat — it does NOT send the command
 *     directly. It calls back so the caller can raise the always-on confirm
 *     dialog (§5.2: the client always confirms an irreversible command). The
 *     GM's own seat gets no button (CMD-11: self-removal is not a Phase 1
 *     affordance).
 *
 * Pure render + callbacks; no net/ coupling.
 */

import type { RoomState, Seat } from "@asarimitsuha/table-core";

export type SeatPresence = "connected" | "reconnecting" | "away";

export function seatPresence(seat: Seat): SeatPresence {
  if (seat.connected) return "connected";
  // reconnecting = dropped non-consented, still inside the grace window.
  return seat.reconnecting ? "reconnecting" : "away";
}

function presenceSuffix(presence: SeatPresence): string {
  switch (presence) {
    case "connected":
      return "";
    case "reconnecting":
      return " · reconnecting…";
    case "away":
      return " · away";
  }
}

function seatLabel(seat: Seat, isOwn: boolean, presence: SeatPresence): string {
  const role = seat.role === "gm" ? " (GM)" : "";
  const you = isOwn ? " — you" : "";
  return `${seat.name}${role}${you}${presenceSuffix(presence)}`;
}

export interface SeatRosterOptions {
  /** This browser's seat id, to mark "you" and to suppress its remove button. */
  ownSeatId: string | null;
  /** Whether the viewer holds the GM role (shows per-seat remove buttons). */
  viewerIsGm: boolean;
}

export interface SeatRosterCallbacks {
  /** GM asked to remove a seat. The caller MUST route this through the confirm
   *  dialog — the roster never sends removeSeat itself (§5.2 three-layer rule:
   *  the client always confirms). */
  onRequestRemoveSeat(seat: Seat): void;
}

const NO_OP: SeatRosterCallbacks = { onRequestRemoveSeat: () => {} };

function sortSeats(seats: Record<string, Seat>): Seat[] {
  return Object.values(seats).sort((a, b) =>
    a.role === b.role ? a.name.localeCompare(b.name) : a.role === "gm" ? -1 : 1,
  );
}

/**
 * Render the roster into `root`. Idempotent (replaces children). Callbacks
 * default to no-ops so a read-only mount needs no wiring.
 */
export function renderSeatRoster(
  root: HTMLElement,
  state: RoomState,
  options: SeatRosterOptions,
  cb: SeatRosterCallbacks = NO_OP,
): void {
  root.replaceChildren();

  const heading = document.createElement("h2");
  heading.textContent = state.name;
  root.appendChild(heading);

  const list = document.createElement("ul");
  list.dataset["testid"] = "seat-list";
  list.setAttribute("aria-label", "Seats");

  for (const seat of sortSeats(state.seats)) {
    const isOwn = seat.id === options.ownSeatId;
    const presence = seatPresence(seat);

    const item = document.createElement("li");
    item.dataset["seatId"] = seat.id;
    item.dataset["role"] = seat.role;
    item.dataset["connected"] = String(seat.connected);
    item.dataset["reconnecting"] = String(seat.reconnecting);
    item.dataset["presence"] = presence;
    item.dataset["name"] = seat.name;

    const label = document.createElement("span");
    label.className = "seat-label";
    label.textContent = seatLabel(seat, isOwn, presence);
    item.appendChild(label);

    // GM management: a remove button on every seat but the GM's own.
    if (options.viewerIsGm && !isOwn) {
      const remove = document.createElement("button");
      remove.type = "button";
      remove.dataset["testid"] = "remove-seat";
      remove.dataset["seatId"] = seat.id;
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", `Remove ${seat.name}`);
      remove.addEventListener("click", () => cb.onRequestRemoveSeat(seat));
      item.appendChild(remove);
    }

    list.appendChild(item);
  }
  root.appendChild(list);
}

/** Connection-status pill (unchanged from M1; kept here so the roster module
 *  is self-contained). Creates the element once, updates it in place. */
export function renderConnectionStatus(root: HTMLElement, status: string): void {
  let el = root.querySelector<HTMLElement>('[data-testid="connection"]');
  if (!el) {
    el = document.createElement("span");
    el.dataset["testid"] = "connection";
    root.appendChild(el);
  }
  el.dataset["status"] = status;
  el.textContent = status;
}
